import { useState } from "react";
import {
  AiPermissionsSection,
  ProjectForm,
  SupportingDocumentsSection,
  areSectionsValid,
  formValuesFromProject,
  isSectionEnabled,
  isSectionRequired,
  membersAfterEdit,
  toCreatePayload,
  toUpdatePayload,
  useAiPermissionOptions,
  useAppSections,
  useCreateProject,
  useProject,
  useProjectMembers,
  useProjectServiceUser,
  useSaveProject,
  useUploadPendingSupportingDocuments,
  type PendingSupportingDocument,
  type Project,
  type ProjectFormValues,
} from "@lumanity/ui/projects";
import { ErrorBanner } from "@lumanity/ui/primitives";
import { ROLE_PROPERTY_KEYS } from "@/features/team/teamApi";
import { MODEL_LABELS } from "@/lib/models";

interface ProjectSetupFormProps {
  /** Edit this project; omit to create one (the signed-in user becomes its owner). */
  projectId?: string;
  /** After the project was saved. `warning` is set when it saved but some supporting documents didn't upload. */
  onSaved: (project: Project, warning?: string) => void;
  onCancel?: () => void;
}

/**
 * The library's ProjectSetup (same create / edit saving), plus the optional
 * project sections the app has turned on in the project service:
 *
 * - AI permissions: confirmed on create and sent with it (the service only
 *   accepts them then); shown read-only on edit.
 * - Supporting documents: picked on create and uploaded once the project
 *   exists; on edit they upload straight away.
 */
const ProjectSetupForm = ({ projectId, onSaved, onCancel }: ProjectSetupFormProps) => {
  const isEdit = !!projectId;
  const serviceUser = useProjectServiceUser();
  const project = useProject(projectId);
  const members = useProjectMembers(projectId);
  const sections = useAppSections();
  const aiEnabled = isSectionEnabled(sections.data, "ai_permissions");
  const docsEnabled = isSectionEnabled(sections.data, "supporting_documents");
  const aiOptions = useAiPermissionOptions({ enabled: aiEnabled && !isEdit });
  const createProject = useCreateProject();
  const saveProject = useSaveProject();
  const uploadPending = useUploadPendingSupportingDocuments();

  const [aiPermissions, setAiPermissions] = useState<string[]>([]);
  const [pendingDocs, setPendingDocs] = useState<PendingSupportingDocument[]>([]);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadError = serviceUser.error ?? project.error ?? members.error ?? sections.error;
  if (loadError) return <ErrorBanner message={`Could not load the project: ${loadError.message}`} />;
  if (!serviceUser.data || !sections.data || (isEdit && (!project.data || !members.data))) {
    return <div className="py-8 text-muted-foreground">Loading project...</div>;
  }

  const create = async (values: ProjectFormValues) => {
    const valid = areSectionsValid(sections.data, {
      aiPermissionOptions: aiOptions.data,
      aiPermissions,
      supportingDocuments: pendingDocs.length,
    });
    if (!valid) {
      setSectionError(
        aiEnabled && isSectionRequired(sections.data, "ai_permissions")
          ? "Confirm every AI permission statement (and add any required supporting documents) before creating the project."
          : "Add the required supporting documents before creating the project.",
      );
      return;
    }
    setSectionError(null);
    const payload = toCreatePayload(values, { currentUserId: serviceUser.data.id });
    const created = await createProject.mutateAsync(aiEnabled ? { ...payload, ai_permissions: aiPermissions } : payload);

    // The project stands even if a file fails to upload; the user can add it from the edit page.
    let warning: string | undefined;
    if (pendingDocs.length > 0) {
      const { failed } = await uploadPending.mutateAsync({ projectId: created.id, pending: pendingDocs });
      if (failed.length > 0) {
        warning = `${failed.length} supporting document(s) failed to upload: ${failed.map((f) => f.file.name).join(", ")}. Add them from Edit project setup.`;
      }
    }
    onSaved(created, warning);
  };

  const save = async (values: ProjectFormValues) => {
    const current = project.data!;
    const changes = toUpdatePayload(values, current);
    const memberList = membersAfterEdit(members.data ?? [], values.member_ids);
    if (!changes && !memberList) {
      onSaved(current);
      return;
    }
    onSaved(await saveProject.mutateAsync({ projectId: current.id, changes, members: memberList }));
  };

  const handleSubmit = async (values: ProjectFormValues) => {
    setIsSubmitting(true);
    try {
      await (isEdit ? save(values) : create(values));
    } catch {
      // The mutation's error is shown in the footer.
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveError = (isEdit ? saveProject.error : createProject.error)?.message ?? sectionError;
  const lockedMemberIds = isEdit
    ? (members.data ?? []).filter((m) => m.role === "owner").map((m) => m.user_id)
    : [serviceUser.data.id];

  return (
    <ProjectForm
      key={projectId ?? "new"}
      mode={isEdit ? "edit" : "create"}
      // Workflow roles are project properties too, but they're user ids managed in the Team panel.
      hiddenProperties={ROLE_PROPERTY_KEYS}
      propertyOptionLabels={{ model: MODEL_LABELS }}
      initialValues={isEdit ? formValuesFromProject(project.data!) : undefined}
      lockedMemberIds={lockedMemberIds}
      role={isEdit ? project.data!.my_role ?? null : "owner"}
      isAdmin={serviceUser.data.is_admin}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      submitLabel={isEdit ? "Save Changes" : "Create Project"}
      // Shown in the order the app's section config gives, after the team.
      optionalSections={{
        supporting_documents: docsEnabled && (
          <SupportingDocumentsSection
            projectId={projectId}
            pending={isEdit ? undefined : pendingDocs}
            onPendingChange={isEdit ? undefined : setPendingDocs}
            required={isSectionRequired(sections.data, "supporting_documents")}
          />
        ),
        ai_permissions: aiEnabled && (
          <AiPermissionsSection
            projectId={projectId}
            value={isEdit ? undefined : aiPermissions}
            onChange={isEdit ? undefined : setAiPermissions}
            required={isSectionRequired(sections.data, "ai_permissions")}
          />
        ),
      }}
      footer={<ErrorBanner className="mb-6" message={saveError} />}
    />
  );
};

export default ProjectSetupForm;
